import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Package,
  ChevronLeft,
  ChevronRight,
  Copy,
  ClipboardList,
  X
} from "lucide-react";

interface Product {
  id: number;
  code: string;
  partNumber: string;
  package: string;
  packaging: string;
  manufacturer: string;
  purchasePrice: number;
  salePrice: number;
  seller: string;
  category: string;
}

export const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("پارت نامبر");
  const [sortBy, setSortBy] = useState("شناسه");
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedCategory, setSelectedCategory] = useState<string>("همه دسته‌ها");

  useEffect(() => {
    if (location.state && location.state.category) {
      setSelectedCategory(location.state.category);
    }
  }, [location]);

  // Mock Data
  const mockData: Product[] = useMemo(() => {
    const data: Product[] = [];
    const categories = ["مقاومت", "خازن", "سلف", "دیود", "ترانزیستور"];
    for (let i = 1; i <= 150; i++) {
      data.push({
        id: i,
        code: `PR${String(i).padStart(4, "0")}`,
        partNumber: `PN-${Math.floor(Math.random() * 10000)}`,
        category: categories[Math.floor(Math.random() * categories.length)],
        package: ["SMD", "DIP", "QFN", "BGA"][Math.floor(Math.random() * 4)],
        packaging: ["Reel", "Tube", "Tray", "Bulk"][
          Math.floor(Math.random() * 4)
        ],
        manufacturer: ["TI", "ST", "Microchip", "NXP"][
          Math.floor(Math.random() * 4)
        ],
        purchasePrice: Math.random() * 1000 + 5,
        salePrice: Math.random() * 1500 + 10,
        seller: ["Supplier A", "Company B", "Vendor C"][
          Math.floor(Math.random() * 3)
        ],
      });
    }
    return data;
  }, []);

  const filteredData = mockData
    .filter((item) => {
      if (selectedCategory !== "همه دسته‌ها" && item.category !== selectedCategory) {
        return false;
      }
      const term = searchTerm.toLowerCase();
      if (!term) return true;
      switch (searchField) {
        case "شناسه":
          return item.code.toLowerCase().includes(term);
        case "پارت نامبر":
          return item.partNumber.toLowerCase().includes(term);
        case "پکیج":
          return item.package.toLowerCase().includes(term);
        case "بسته بندی":
          return item.packaging.toLowerCase().includes(term);
        case "سازنده":
          return item.manufacturer.toLowerCase().includes(term);
        case "فروشنده":
          return item.seller.toLowerCase().includes(term);
        default:
          return (
            item.code.toLowerCase().includes(term) ||
            item.partNumber.toLowerCase().includes(term) ||
            item.manufacturer.toLowerCase().includes(term) ||
            item.package.toLowerCase().includes(term) ||
            item.packaging.toLowerCase().includes(term) ||
            item.seller.toLowerCase().includes(term)
          );
      }
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "شناسه":
          return a.code.localeCompare(b.code);
        case "پارت نامبر":
          return a.partNumber.localeCompare(b.partNumber);
        case "پکیج":
          return a.package.localeCompare(b.package);
        case "سازنده":
          return a.manufacturer.localeCompare(b.manufacturer);
        default:
          return 0;
      }
    });

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentData = filteredData.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(price);
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden flex flex-col h-[calc(100vh-120px)]">
      {selectedCategory !== "همه دسته‌ها" && (
        <div className="bg-blue-50 dark:bg-blue-900/20 px-6 py-3 border-b border-blue-100 dark:border-blue-800/30 flex items-center justify-between shrink-0">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            نمایش محصولات برای دسته‌بندی <strong className="font-bold">{selectedCategory}</strong>
          </p>
          <button 
            onClick={() => {
              navigate("/admin/categories");
            }}
            className="p-1 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-800/50 rounded-md transition-colors"
            title="بازگشت به دسته‌بندی‌ها"
          >
            <X size={18} />
          </button>
        </div>
      )}
      <div className="p-6 border-b border-slate-100 dark:border-slate-700/50 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 shrink-0">
        <div className="flex w-full xl:w-auto items-center gap-6">
          <button 
            onClick={() => {
              navigate("/admin/products/new", { state: { category: selectedCategory !== "همه دسته‌ها" ? selectedCategory : "" } });
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 shrink-0 transition-colors shadow-sm"
          >
            <Plus size={18} />
            <span>کالای جدید</span>
          </button>
          
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 hidden md:block"></div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 dark:text-slate-400">مرتب‌سازی:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm py-2 px-3 focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
            >
              <option value="شناسه">شناسه</option>
              <option value="پارت نامبر">پارت نامبر</option>
              <option value="پکیج">پکیج</option>
              <option value="سازنده">سازنده</option>
            </select>
          </div>
        </div>

        <div className="flex w-full xl:w-auto items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span>جستجو در:</span>
            <select
              value={searchField}
              onChange={(e) => {
                setSearchField(e.target.value);
                setCurrentPage(1);
              }}
              className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm py-2 px-3 focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
            >
              <option value="شناسه">شناسه</option>
              <option value="پارت نامبر">پارت نامبر</option>
              <option value="پکیج">پکیج</option>
              <option value="بسته بندی">بسته بندی</option>
              <option value="سازنده">سازنده</option>
              <option value="فروشنده">فروشنده</option>
            </select>
          </div>
          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="جستجوی کالا..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
              className="w-full pl-4 pr-10 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
            />
            <Search
              size={18}
              className="absolute right-3 top-2.5 text-slate-400"
            />
          </div>
        </div>
      </div>

      <div className="overflow-auto flex-1" dir="ltr">
        <table className="w-full text-left max-h-min table-auto">
          <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 text-xs sticky top-0 z-10 shadow-sm whitespace-nowrap">
            <tr>
              <th className="px-4 py-4 font-medium w-16 text-center">#</th>
              <th className="px-4 py-4 font-medium w-24">شناسه</th>
              <th className="px-4 py-4 font-medium w-full min-w-[200px]">پارت نامبر</th>
              <th className="px-4 py-4 font-medium">پکیج</th>
              <th className="px-4 py-4 font-medium">بسته بندی</th>
              <th className="px-4 py-4 font-medium">سازنده</th>
              <th className="px-4 py-4 font-medium">فی خرید</th>
              <th className="px-4 py-4 font-medium">فی فروش</th>
              <th className="px-4 py-4 font-medium">فروشنده</th>
              <th className="px-4 py-4 font-medium text-right">عملیات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
            {currentData.length > 0 ? (
              currentData.map((item, index) => (
                <tr
                  key={item.id}
                  onDoubleClick={() => navigate(`/admin/products/${item.id}`)}
                  className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors text-sm dark:text-slate-300 cursor-pointer"
                >
                  <td className="px-4 py-3 text-center text-slate-500 whitespace-nowrap truncate max-w-[50px]">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap truncate max-w-[100px]">{item.code}</td>
                  <td className="px-4 py-3 whitespace-nowrap truncate w-full min-w-[200px] max-w-[300px]">{item.partNumber}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap truncate max-w-[80px]">
                    {item.package}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap truncate max-w-[80px]">
                    {item.packaging}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap truncate max-w-[100px]">{item.manufacturer}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300 whitespace-nowrap truncate max-w-[120px]">
                    {formatPrice(item.purchasePrice)}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300 whitespace-nowrap truncate max-w-[120px]">
                    {formatPrice(item.salePrice)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap truncate max-w-[120px]">{item.seller}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        className="p-1.5 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 rounded-lg transition-colors"
                        title="افزودن کالای مشابه"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        className="p-1.5 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                        title="ویرایش"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className="p-1.5 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-lg transition-colors"
                        title="کاردکس"
                      >
                        <ClipboardList size={16} />
                      </button>
                      <button
                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                        title="حذف"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={10}
                  className="px-6 py-16 text-center text-slate-500 dark:text-slate-400"
                >
                  <Package
                    size={48}
                    className="mx-auto mb-4 text-slate-300 dark:text-slate-600"
                  />
                  <p>کالایی برای نمایش وجود ندارد.</p>
                  <button className="text-blue-600 hover:underline mt-2 text-sm font-medium">
                    افزودن اولین کالا
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="p-4 border-t border-slate-100 dark:border-slate-700/50 flex flex-col sm:flex-row justify-between items-center gap-4 bg-white dark:bg-slate-800 shrink-0">
        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <span>نمایش</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-slate-800 dark:text-white px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>آیتم در صفحه</span>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
            نمایش {filteredData.length === 0 ? 0 : startIndex + 1} تا{" "}
            {Math.min(startIndex + pageSize, filteredData.length)} از{" "}
            {filteredData.length}
          </span>
          <div className="flex items-center gap-1" dir="ltr">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-1 rounded bg-slate-50 hover:bg-slate-100 disabled:opacity-50 disabled:hover:bg-slate-50 text-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="px-3 py-1 text-sm font-medium text-slate-700 dark:text-slate-200">
              {currentPage} / {totalPages > 0 ? totalPages : 1}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded bg-slate-50 hover:bg-slate-100 disabled:opacity-50 disabled:hover:bg-slate-50 text-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
